import * as React from "react";
import { useState, useEffect } from "react";

const styles = {
  text: {
    width: "100%",
    height: "min-content",
    fontSize: "13px",
    fontFamily: "inherit",
    backgroundColor: "unset",
    outlineStyle: "none" as any,
    color: "#666",
    border: "0",
    padding: 0,
    margin: 0,
    lineHeight: "1rem",
  },
  textarea: {
    verticalAlign: "inherit",
    resize: "none" as any,
    overflow: "hidden",
  },
  scrollOnFocus: {
    "&:focus": {
      overflow: "unset",
    },
  },
};

interface IProps
  extends React.DetailedHTMLProps<
    React.TextareaHTMLAttributes<HTMLTextAreaElement>,
    HTMLTextAreaElement
  > {
  /** Habilita/desabilita o textarea para alterar a descrição. */
  selected?: boolean;
  /** Altura inicial da linha em que a célula pertence. */
  rowHeight: number;
  /** Altura padrão para célula, também usada como altura mínima do componente. */
  defaultRowHeight?: number;
  /** Evento disparado ao se redefinir a altura da célula. */
  onResizeHeight?: () => void;
  /** Evento disparado ao obter a referência ao elemento DOM do textarea. */
  onGetTextAreaRef?: (element: HTMLTextAreaElement) => void;
}

/** Este componente renderiza um elemento p quando não estiver em foco e um textarea quando estiver
 * em foco para ajustar sua altura de acordo com o texto e/ou o estilo definido para o mesmo.
 */
const DescriptionCell = ({
  id,
  selected = false,
  rowHeight,
  style,
  className,
  value,
  defaultValue,
  onChange,
  onFocus,
  onBlur,
  defaultRowHeight = 24,
  onResizeHeight = () => {},
  onGetTextAreaRef = () => {},
  ...props
}: IProps) => {
  rowHeight =
    !rowHeight || rowHeight < defaultRowHeight ? defaultRowHeight : rowHeight;
  const [textAreaRef, setTextAreaRef] = useState<HTMLTextAreaElement>();
  const [textAreaHeight, setTextAreaHeight] = useState(rowHeight);
  const [textAreaValue, setTextAreaValue] = useState(value);

  const [paragraphRef, setParagraphRef] = useState<HTMLParagraphElement>();
  const [paragraphHeight, setParagraphHeight] = useState(rowHeight);

  const [changed, setChanged] = useState(false);
  const [isInput, setIsInput] = useState(false);

  useEffect(() => {
    if (value !== textAreaValue) {
      setTextAreaValue(value);
    }
  }, [value]);

  // Atualiza a altura do textarea quando a célula estiver selecionada
  // para ficar na mesma altura do texto anterior.
  useEffect(() => {
    if (selected) {
      if (paragraphHeight !== textAreaHeight) {
        setTextAreaHeight(paragraphHeight);
      }
    }

    if (isInput && !selected && rowHeight - 1 !== textAreaHeight) {
      onResizeHeight();
    }

    if (isInput !== selected) setIsInput(selected);
  }, [selected, rowHeight]);

  const handleBlur = (ev) => {
    if (onBlur) onBlur(ev);

    if (changed) {
      // Atualiza a altura do textarea para se adequar ao tamanho do texto editado.
      if (textAreaRef && textAreaRef.scrollHeight !== textAreaHeight) {
        setTextAreaHeight(textAreaRef.scrollHeight);
      }
      setChanged(false);
    }
  };

  const handleChange = (event) => {
    const { value } = event.target;
    setChanged(true);
    setTextAreaValue(value || "");
    if (onChange) onChange(event);
  };

  const handleTextAreaRef = (el: any) => {
    if (el && el instanceof HTMLTextAreaElement) {
      if (el !== textAreaRef) {
        setTextAreaRef(el);
        onGetTextAreaRef(el);
        el.focus();
      }
    }
  };

  const handleParagraphRef = (el) => {
    if (el && el instanceof HTMLParagraphElement) {
      if (paragraphHeight !== el.offsetHeight) {
        setParagraphHeight(el.offsetHeight);
      }

      if (el !== paragraphRef) {
        setParagraphRef(el);
      }
    }
  };

  // Não deve apresentar a textarea se antes não renderizou o p
  // pq se não, não vai ter a altura correta pré-definida
  if (isInput) {
    return (
      <textarea
        {...props}
        id={id}
        ref={handleTextAreaRef}
        className={className}
        value={textAreaValue}
        onFocus={onFocus}
        onBlur={handleBlur}
        onChange={handleChange}
        style={{
          ...styles.text,
          ...styles.textarea,
          ...style,
          height: textAreaHeight,
          minHeight: defaultRowHeight,
        }}
      />
    );
  }

  return (
    <p
      id={id}
      ref={handleParagraphRef}
      className={className}
      style={{
        ...styles.text,
        ...style,
        minHeight: defaultRowHeight,
        wordBreak: "break-word",
      }}
    >
      {value || defaultValue}
    </p>
  );
};

export default DescriptionCell;



// WEBPACK FOOTER //
// ./src/modules/WBS/components/layouts/DescriptionCell.tsx